/**
 * Small reusable "translate" icon button — calls the user's direct Gemini API
 * via /api/translate and writes the result via onTranslated.
 *
 * Usage:
 *   <TranslateButton
 *     sourceText={nameAr}
 *     direction="ar_to_en"
 *     onTranslated={(out) => setNameEn(out)}
 *   />
 *
 * The button shows a spinner while the request is in-flight and a small
 * toast on failure.  Wrap-around — never throws.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Languages, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { useAiFlag } from '../utils/useAiFlag';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function TranslateButton({
  sourceText,
  direction,                     // "ar_to_en" | "en_to_ar"
  onTranslated,
  disabled = false,
  testId,
  size = 'md',                   // 'sm' | 'md'
  flagId = 'auto_translation',   // Cloud GM AI Control Hub feature id
}) {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const { enabled: aiEnabled, loading: flagLoading } = useAiFlag(flagId);

  // Feature gated by Cloud GM AI Control Hub — when off, hide the button entirely
  if (!flagLoading && !aiEnabled) return null;

  const handleClick = async () => {
    const text = (sourceText || '').trim();
    if (!text) {
      toast.error(language === 'ar' ? 'لا يوجد نص للترجمة' : 'No text to translate');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/translate`,
        { text, direction },
        { headers: getAuthHeaders() },
      );
      const out = (res.data?.translated || '').trim();
      if (out) {
        onTranslated(out);
        toast.success(language === 'ar' ? '✨ تُرجم بنجاح' : '✨ Translated');
      } else {
        toast.error(language === 'ar' ? 'تُرجم لنص فارغ' : 'Empty translation');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || (language === 'ar' ? 'فشل في الترجمة' : 'Translation failed'));
    } finally {
      setLoading(false);
    }
  };

  const sizeClass = size === 'sm' ? 'w-7 h-7' : 'w-9 h-9';
  const iconClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || loading || !sourceText?.trim()}
      title={
        direction === 'ar_to_en'
          ? language === 'ar' ? 'ترجمة تلقائية إلى الإنجليزية' : 'Auto-translate to English'
          : language === 'ar' ? 'ترجمة تلقائية إلى العربية' : 'Auto-translate to Arabic'
      }
      className={`${sizeClass} flex items-center justify-center rounded-lg border transition-all
        ${loading
          ? 'bg-[#d4af37]/20 border-[#d4af37]/40 cursor-wait'
          : 'bg-[#d4af37]/10 border-[#d4af37]/30 hover:bg-[#d4af37]/25 hover:border-[#d4af37]/60'}
        ${(disabled || !sourceText?.trim()) ? 'opacity-40 cursor-not-allowed' : ''}
      `}
      data-testid={testId || `translate-btn-${direction}`}
    >
      {loading
        ? <Loader2 className={`${iconClass} animate-spin text-[#d4af37]`} />
        : <Languages className={`${iconClass} text-[#d4af37]`} />}
    </button>
  );
}
